import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const SECTIONS = [
  {
    title: "1. Introduction",
    blocks: [
      { type: "p", text: "NeXstrength Business (“NeXstrength,” “we,” “our,” or “us”) respects your privacy and is committed to protecting the personal information you share with us through our website, advertisements, consultation forms, and other communication channels." },
      { type: "p", text: "This Privacy Policy explains how we collect, use, store, and protect your information when you interact with our services." },
    ],
  },
  {
    title: "2. Information We Collect",
    blocks: [
      { type: "p", text: "We may collect the following information:" },
      {
        type: "ul",
        items: [
          "Contact information: Name, phone number, email address, and other contact details you provide.",
          "Business information: Business type, approximate revenue range, business challenges, and information shared during consultations.",
          "Communication information: Messages, inquiries, feedback, and communications with our team.",
          "Technical information: Certain device, browser, usage, and website interaction information collected through cookies or analytics tools, where applicable.",
        ],
      },
      { type: "p", text: "We aim to collect only information reasonably necessary for our business purposes." },
    ],
  },
  {
    title: "3. How We Use Your Information",
    blocks: [
      { type: "p", text: "We may use your information to:" },
      {
        type: "ul",
        items: [
          "Respond to inquiries and consultation requests.",
          "Understand your business needs and provide relevant consultancy services.",
          "Communicate about our programs, services, and offers.",
          "Improve our website, services, and customer experience.",
          "Manage registrations, appointments, and customer relationships.",
          "Maintain business records and comply with applicable legal obligations.",
        ],
      },
      { type: "p", text: "We will not use your information for purposes that are incompatible with this policy or applicable law." },
    ],
  },
  {
    title: "4. Advertising and Analytics",
    blocks: [
      { type: "p", text: "We may use advertising and analytics tools, including Meta advertising technologies, to understand campaign performance, measure website activity, and improve the relevance of our marketing." },
      { type: "p", text: "Where required, we will provide appropriate notices and obtain consent for cookies or tracking technologies." },
      { type: "p", text: "You can manage available advertising and privacy preferences through the relevant platforms and your browser settings." },
    ],
  },
  {
    title: "5. Sharing of Information",
    blocks: [
      { type: "p", text: "We do not sell personal information as a standalone product." },
      { type: "p", text: "We may share information with trusted service providers who help us operate our business, such as website hosting providers, form and CRM platforms, communication services, analytics providers, and payment processors, where applicable." },
      { type: "p", text: "We may also disclose information when required by law, to protect our legal rights, or in connection with a legitimate business transaction, subject to applicable legal requirements." },
    ],
  },
  {
    title: "6. Data Security and Retention",
    blocks: [
      { type: "p", text: "We take reasonable technical and organizational measures to protect personal information against unauthorized access, loss, misuse, or disclosure." },
      { type: "p", text: "We retain information only for as long as reasonably necessary for the purposes described in this policy, including applicable legal, accounting, and recordkeeping requirements." },
      { type: "p", text: "No method of electronic storage or transmission can be guaranteed to be completely secure." },
    ],
  },
  {
    title: "7. Your Privacy Rights",
    blocks: [
      { type: "p", text: "Depending on applicable law, you may have the right to request access to, correction of, or deletion of your personal information, withdraw consent where processing is based on consent, or raise a privacy-related concern." },
      { type: "p", text: "To make a request, please contact us using the details below." },
      { type: "p", text: "We may need to verify your identity before processing certain requests." },
    ],
  },
  {
    title: "8. Third-Party Websites and Services",
    blocks: [
      { type: "p", text: "Our website or communications may contain links to third-party websites or services. Their privacy practices are governed by their own policies, and we encourage you to review those policies before sharing personal information." },
    ],
  },
  {
    title: "9. Children's Privacy",
    blocks: [
      { type: "p", text: "Our services are intended for adults and business owners. We do not knowingly seek to collect personal information from children in circumstances where such collection is prohibited by applicable law." },
    ],
  },
  {
    title: "10. Changes to This Policy",
    blocks: [
      { type: "p", text: "We may update this Privacy Policy from time to time to reflect changes in our services, technology, or legal obligations. Any revised version will be published on our website with an updated revision date." },
    ],
  },
  {
    title: "11. Contact Us",
    blocks: [
      { type: "p", text: "For questions, privacy requests, or concerns regarding this Privacy Policy, please contact:" },
      { type: "p", text: "Email: nexstrengthbusiness@gmail.com" },
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]" data-testid="privacy-page">
      <main className="mx-auto w-full max-w-[680px] px-5 pb-20 pt-10 sm:pt-14">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 text-[13px] font-bold text-neutral-400 transition-colors hover:text-[#F25C05]"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <h1 className="mt-6 text-[clamp(1.8rem,6vw,2.5rem)] font-extrabold leading-[1.15] tracking-[-0.02em]">
          Privacy Policy — NeXstrength Business
        </h1>
        <p className="mt-2.5 text-[13px] font-semibold text-neutral-400">Last Updated: 20 October 2026</p>

        <div className="mt-8 flex flex-col gap-6">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="text-[16.5px] font-extrabold tracking-tight">{s.title}</h2>
              {s.blocks.map((b, i) =>
                b.type === "ul" ? (
                  <ul key={i} className="mt-2 flex flex-col gap-1.5">
                    {b.items.map((it) => (
                      <li key={it} className="flex gap-2.5 text-[14px] font-medium leading-[1.65] text-[#333333]">
                        <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#F25C05]" />
                        {it}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p key={i} className="mt-2 text-[14px] font-medium leading-[1.7] text-[#333333]">
                    {b.text}
                  </p>
                )
              )}
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-[18px] border border-neutral-100 p-5 text-center shadow-[0_10px_30px_-18px_rgba(0,0,0,0.2)]">
          <p className="text-[13px] font-bold">Questions about this policy?</p>
          <a href="mailto:nexstrengthbusiness@gmail.com" className="mt-1 inline-block text-[13.5px] font-bold text-[#F25C05]">
            nexstrengthbusiness@gmail.com
          </a>
        </div>
      </main>
    </div>
  );
}
